import { isPointInConvexPolygon } from 'libs/utils/geometry/relativePositions';
import { MapMouseEvent } from 'mapbox-gl';
import { RefObject, useEffect, useRef, useState } from 'react';
import { IXY } from 'react-give-me-map/dist/utils/map/mapTypes';
import { MapRef } from 'react-map-gl';

interface Props {
    xyCoordinates: IXY[];
    mapRef: RefObject<MapRef>;
    onDrag: (diffX: number, diffY: number) => void;
    onDragEnd: (diffX: number, diffY: number) => void;
}

const getLeftTop = (xyCoordinates: IXY[]): IXY =>
    xyCoordinates.reduce((acc, curr) => {
        return { x: Math.min(acc.x, curr.x), y: Math.min(acc.y, curr.y) };
    });
const getRightBottom = (xyCoordinates: IXY[]): IXY =>
    xyCoordinates.reduce((acc, curr) => {
        return { x: Math.max(acc.x, curr.x), y: Math.max(acc.y, curr.y) };
    });
const getOffsetBy = (xyCoordinates: IXY[], xOffset: number, yOffset: number) =>
    xyCoordinates.map(({ x, y }) => ({
        x: x + xOffset,
        y: y + yOffset
    }));

export const MoveArea = (props: Props) => {
    const [isMoving, setIsMoving] = useState(false);
    const initialCenterRef = useRef<IXY>(null);

    const leftTop = getLeftTop(props.xyCoordinates);
    const rightBottom = getRightBottom(props.xyCoordinates);
    const width = rightBottom.x - leftTop.x;
    const height = rightBottom.y - leftTop.y;

    const coordinates = getOffsetBy(props.xyCoordinates, -leftTop.x, -leftTop.y);

    useEffect(() => {
        const container = props.mapRef.current?.getContainer()?.getBoundingClientRect();
        const offsetX = container?.left ?? 0;
        const offsetY = container?.top ?? 0;

        function mouseMove(event: MouseEvent) {
            if (isMoving) {
                const diffX = event.clientX - initialCenterRef.current.x - offsetX;
                const diffY = event.clientY - initialCenterRef.current.y - offsetY;

                props.onDrag(diffX, diffY);
                event.preventDefault();
            }
        }

        function mouseUp(event: MouseEvent) {
            if (isMoving) {
                setIsMoving(false);
                const diffX = event.clientX - initialCenterRef.current.x - offsetX;
                const diffY = event.clientY - initialCenterRef.current.y - offsetY;

                props.onDragEnd(diffX, diffY);
            }
        }

        function mouseDown(e: MapMouseEvent) {
            const point = { x: e.point.x, y: e.point.y };
            const isInside = isPointInConvexPolygon(point, props.xyCoordinates);
            if (isInside) {
                e.preventDefault();
                initialCenterRef.current = point;
                setIsMoving(true);
            }
        }

        document.addEventListener('mousemove', mouseMove);
        document.addEventListener('mouseup', mouseUp);
        props.mapRef?.current?.on('mousedown', mouseDown);

        return () => {
            document.removeEventListener('mousemove', mouseMove);
            document.removeEventListener('mouseup', mouseUp);
            props.mapRef?.current?.off('mousedown', mouseDown);
        };
    }, [isMoving, props.xyCoordinates]);

    return (
        <div
            style={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                top: 0,
                cursor: 'move'
            }}
        >
            <svg width={width} height={height}>
                <path
                    className={'handle'}
                    style={{
                        strokeWidth: 4,
                        stroke: 'red',
                        fill: '#000',
                        opacity: 0,
                        overflowY: 'hidden',
                        position: 'relative',
                        cursor: 'move'
                    }}
                    d={
                        [...coordinates, coordinates[0]]
                            .map((c, i) => (i ? `${c.x} ${c.y}` : `M${c.x} ${c.y}`))
                            .join(' ') + 'Z'
                    }
                />
            </svg>
        </div>
    );
};
